import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { Tabs, Tab } from 'carbon-components-react';
import {
  currentUser,
  selectScreenSize,
  selectUserProcess
} from 'redux/session/selectors';
import { currentUserOrganisation } from 'redux/organisation/selectors';
import { USERSROLE, APP_URL } from 'types';
import { Account, Profile, Configuration } from './components';
import { useSaga } from 'use-reducer-saga';
import { useComponentMount } from 'utilities/hooks.util';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  unlinkDBYDStart,
  updateUserPreferencesStart,
  updateConfigurationStart,
  invalidatePermalinkStart,
  setEmailLinkingStart
} from './localStore/actions';
import { fetchUserStart } from 'redux/session/actions';
import { fetchUserOrganisationStart } from 'redux/organisation/actions';
import _ from 'lodash';

const Settings: React.FC = () => {
  const user = useSelector(currentUser);
  const userProcess = useSelector(selectUserProcess);
  const organisation = useSelector(currentUserOrganisation);
  const screenSize = useSelector(selectScreenSize);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const dispatch = useDispatch();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tab = queryParams.get('tab');

  const {
    unlinkDBYDResponse,
    isUnlinkingDBYD,
    isUpdatingUserPreferences,
    isUpdatingConfiguration,
    configurationUpdateType,
    isSetEmailLinking,
    invalidatePermalinkError,
    invalidatingPermalink,
    setEmailLinkingResponse
  } = localStore;

  const history = useHistory();

  const [isUpdatingProfile, setIsUpdatingProfile] = useState<boolean>(false);
  const [isUpdatingOrganisation, setIsUpdatingOrganisation] =
    useState<boolean>(false);

  const handleClickLinkAccount = () => {
    history.push(APP_URL.ONBOARDING);
  };

  const handleClickUnlinkAccount = () => {
    localDispatch(unlinkDBYDStart({}));
  };

  const handleClickRelinkEmail = () => {
    localDispatch(setEmailLinkingStart({}));
  };

  const handleInvalidatePermalink = () => {
    localDispatch(invalidatePermalinkStart());
  };

  const updateUserProfile = (data: any) => {
    localDispatch(updateUserPreferencesStart(data));
  };

  const onRoleMappingSubmit = (data: any) => {
    const dataToSubmit = [];
    const roleData = _.cloneDeep(organisation.Ssorolemap);
    if (data.userGroup !== undefined) {
      const roleName = 'User';
      const indexRole = _.findIndex(roleData, { role_name: roleName });
      const id = roleData[indexRole].id;
      if (id) {
        const groupData = {
          id: id,
          role_name: roleName,
          name: data.userGroup
        };
        dataToSubmit.push(groupData);
      }
    }
    if (data.managerGroup !== undefined) {
      const roleName = 'Manager';
      const indexRole = _.findIndex(roleData, { role_name: roleName });
      const id = roleData[indexRole].id;
      if (id) {
        const groupData = {
          id: id,
          role_name: roleName,
          name: data.managerGroup
        };
        dataToSubmit.push(groupData);
      }
    }
    if (data.orgAdminGroup !== undefined) {
      const roleName = 'Organisation Admin';
      const indexRole = _.findIndex(roleData, { role_name: roleName });
      const id = roleData[indexRole].id;
      if (id) {
        const groupData = {
          id: id,
          role_name: roleName,
          name: data.orgAdminGroup
        };
        dataToSubmit.push(groupData);
      }
    }
    if (dataToSubmit.length > 0) {
      const payload = {
        data: { roles_mapping: dataToSubmit },
        type: 'ssoRoleMapping'
      };
      localDispatch(updateConfigurationStart(payload));
    }
  };

  const updateOrgConfiguration = (data: any) => {
    const payload = {
      data,
      type: 'others'
    };
    localDispatch(updateConfigurationStart(payload));
  };

  const onEmailDomainSubmit = (data: any) => {
    const payload = {
      data,
      type: 'emailDomains'
    };
    localDispatch(updateConfigurationStart(payload));
  };

  useEffect(() => {
    if (isUpdatingConfiguration) {
      setIsUpdatingOrganisation(true);
    } else if (!isUpdatingConfiguration && isUpdatingOrganisation) {
      dispatch(fetchUserOrganisationStart());
      setIsUpdatingOrganisation(false);
    }
  }, [isUpdatingConfiguration, isUpdatingOrganisation, dispatch]);

  useEffect(() => {
    if (isUpdatingUserPreferences) {
      setIsUpdatingProfile(true);
    } else if (!isUpdatingUserPreferences && isUpdatingProfile) {
      dispatch(fetchUserStart({ process: 'refetch' }));
      setIsUpdatingProfile(false);
    }
  }, [isUpdatingUserPreferences, isUpdatingProfile, dispatch]);

  useEffect(() => {
    if (!isUnlinkingDBYD && unlinkDBYDResponse) {
      dispatch(fetchUserStart({ process: 'refetch' }));
    }
    if (!isSetEmailLinking && setEmailLinkingResponse) {
      dispatch(fetchUserStart({ process: 'refetch' }));
    }
  }, [
    unlinkDBYDResponse,
    isUnlinkingDBYD,
    setEmailLinkingResponse,
    isSetEmailLinking,
    dispatch
  ]);

  useEffect(() => {
    if (tab === 'profile') {
      if (user.role_type_actual === USERSROLE.TRIALUSER) {
        setSelectedTab(1);
      } else {
        setSelectedTab(0);
      }
    }
  }, [tab, user]);

  useComponentMount(() => {
    dispatch(fetchUserStart({ process: 'refetch' }));
  });

  return (
    <div id="Settings" data-testid="Settings" className="settings">
      <FullHeaderLeftSideFullPanelLayout enableHeaderGlobalBar={true}>
        <div className="page-title">Settings</div>
        <div className="tab-container">
          <Tabs type="container" selected={selectedTab}>
            {user.role_type_actual === USERSROLE.TRIALUSER && (
              <Tab id="accountTab" label="Account">
                <Account user={user} userProcess={userProcess} />
              </Tab>
            )}
            <Tab id="profileTab" label="Profile">
              <Profile
                user={user}
                screenSize={screenSize}
                onClickLinkAccount={handleClickLinkAccount}
                onClickUnlinkAccount={handleClickUnlinkAccount}
                unlinkDBYDResponse={unlinkDBYDResponse}
                isUnlinkingDBYD={isUnlinkingDBYD || false}
                updateUserProfile={updateUserProfile}
                isSetEmailLinking={isSetEmailLinking || false}
                onClickRelinkEmail={handleClickRelinkEmail}
              />
            </Tab>
            {user.role_type_actual === USERSROLE.ORGADMIN &&
              user.organisation_id !== null && (
                <Tab id="configurationTab" label="Configuration">
                  <Configuration
                    user={user}
                    screenSize={screenSize}
                    organisation={organisation}
                    handleRoleMappingSubmit={onRoleMappingSubmit}
                    updateOrgConfiguration={updateOrgConfiguration}
                    handleEmailDomainSubmit={onEmailDomainSubmit}
                    handleInvalidatePermalink={handleInvalidatePermalink}
                    invalidatePermalinkError={invalidatePermalinkError}
                    invalidatingPermalink={invalidatingPermalink}
                    configurationUpdateType={configurationUpdateType || ''}
                    isUpdatingConfiguration={isUpdatingConfiguration || false}
                  />
                </Tab>
              )}
          </Tabs>
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default React.memo(Settings);
